import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableWrap, Td, Th } from '@/components/ui/table';
import { type ApiToken, api } from '@/lib/api';

function formatDate(seconds: number | null): string {
  return seconds === null ? '—' : new Date(seconds * 1000).toLocaleDateString('ja-JP');
}

function isExpired(token: ApiToken): boolean {
  return token.expiresAt !== null && token.expiresAt * 1000 <= Date.now();
}

/** 発行済みトークンの一覧と失効。自分のものしか返らない。 */
export function TokenList({ tokens, onRevoked }: { tokens: ApiToken[]; onRevoked: () => void }) {
  const [busy, setBusy] = useState(false);

  async function revoke(id: string) {
    setBusy(true);
    try {
      await api.delete(`/api/tokens/${id}`);
      onRevoked();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border border-line bg-surface-raised">
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>NAME</Th>
              <Th>PREFIX</Th>
              <Th>CREATED</Th>
              <Th>LAST USED</Th>
              <Th>EXPIRES</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {tokens.map((token) => (
              <tr key={token.id}>
                <Td>
                  <div className="flex items-center gap-2">
                    {token.name}
                    {token.revokedAt ? <Badge variant="outline">REVOKED</Badge> : null}
                    {!token.revokedAt && isExpired(token) ? (
                      <Badge variant="outline">EXPIRED</Badge>
                    ) : null}
                  </div>
                </Td>
                <Td className="font-mono text-xs text-ink-subtle">{token.prefix}…</Td>
                <Td className="font-mono text-xs">{formatDate(token.createdAt)}</Td>
                <Td className="font-mono text-xs">{formatDate(token.lastUsedAt)}</Td>
                <Td className="font-mono text-xs">
                  {token.expiresAt === null ? '無期限' : formatDate(token.expiresAt)}
                </Td>
                <Td className="text-right">
                  {/* 期限切れ・失効済みは既に通らないので、押せる操作を出さない */}
                  {token.revokedAt || isExpired(token) ? null : (
                    <Button onClick={() => revoke(token.id)} disabled={busy}>
                      失効
                    </Button>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>
    </div>
  );
}
